import React, { useMemo, useEffect, useRef, useCallback, forwardRef, useImperativeHandle, useState } from 'react';

import { ChromePicker } from 'react-color';

import { Content, ContentTitle, Wrapper, ColorOption, ColorOptions, PickerContent } from './styles';

const ColorPicker = forwardRef(({
  changeBarColor,
  colors,
  hideColorPicker,
}, ref) => {
  // Estados internos ao componente
  const [activeColor, setActiveColor] = useState('#000');
  const [colorPickerIsVisible, setColorPickerIsVisible] = useState(false);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);

  const wrapperRef = useRef();

  // Mapeamento dos estados, aqui o nome do atributo é mapeado para o seu respectivo
  // setState, isso será usado no método externalizado em useImperativeHandle -> setOptions()
  const mappedStates = {
    activeColor: setActiveColor,
    colorPickerIsVisible: setColorPickerIsVisible,
    translateX: setTranslateX,
    translateY: setTranslateY,
  };

  // Externalização de estado e métodos internos ao colorPicker para comunicação entre
  // o gráfico e o colorPicker de forma direta, evitando re-renders top-down.
  useImperativeHandle(ref, () => ({
    activeColor,
    colorPickerIsVisible,
    translateX,
    translateY,
    // Possibilita a atualização do estado interno do color picker de um componente pai
    // que guarda a referência do color picker.
    setOptions: (options) => {
      if (typeof options === 'object') {
        Object.keys(options).forEach((option) => {
          const optionStateSetter = mappedStates[option];
          if (optionStateSetter) {
            optionStateSetter(options[option]);
          }
        });
      }
    },
  }));

  useEffect(() => {
    /**
     * clicked outside handler
     */
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        hideColorPicker();
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef, hideColorPicker]);

  // Como há uma regra de negócio onde o usuario deve ver sempre
  // as cores na ordem em que elas são aplicadas e selecionadas
  // no color picker, se faz necessario fazer uma checagem
  // Se nas cores carregadas, existe a cor selecionada, ou não
  // para decidir qual cor será removida da visão, para que a primeira cor
  // exibida seja sempre a cor selecionada e as demais cores como sendo
  // as cores previamente selecionadas (na ordem de seleção)

  // Mas ao abrir o colorpicker, não deve ser persistidas as cores que ainda não foram selecionadas,
  // esta configuração da nova cor deve ser mantida apenas no primeiro ColorOption exibido (contendo a cor selecionada)

  // Dessa forma, temos o array de cores memorizadas, que correspondem
  // as cores selecionadas anteriormente (excetuando-se da cor selecionada)
  const memorizedColors = useMemo(() => {
    const tempArray = [...colors];
    const selectedColorindex = tempArray.findIndex((tempArrayItem) => tempArrayItem === activeColor);
    // OBS: se a cor selecionada não existir no array, o findIndex retorna -1
    // o Array.prototype.splice recebendo -1 e 1 como parametros vai deletar o ultimo
    // elemento da lista (que é o comportamento desejado)
    tempArray.splice(selectedColorindex, 1);
    return tempArray;
  }, [colors, activeColor]);

  // Aqui temos o elemento da cor selecionada, que vai guardar
  // -> A cor da barra selecionada inicialmente
  // -> Ao configurar uma nova cor a barra, ele exibirá a nova cor configurada
  const renderSelectedColor = useCallback(() => (
    <ColorOption
      active
      color={activeColor}
      onClick={() => changeBarColor(activeColor)}
    />
  ), [activeColor, changeBarColor]);

  return colorPickerIsVisible ? (
    <Wrapper translateX={translateX} translateY={translateY}>
      <PickerContent ref={wrapperRef}>
        <ChromePicker
          color={activeColor}
          onChangeComplete={(color) => changeBarColor(color.hex)}
          disableAlpha
        />
        <Content>
          <ContentTitle>
            OUTRAS CORES
          </ContentTitle>
          <ColorOptions>
            {
              renderSelectedColor()
            }
            {
              memorizedColors.map((color) => (
                <ColorOption
                  active={false}
                  color={color}
                  key={color}
                  onClick={() => changeBarColor(color)}
                />
              ))
            }
          </ColorOptions>
        </Content>
      </PickerContent>
    </Wrapper>
  ) : <></>;
});

export default ColorPicker;
